import { useAuth } from '@agentnet/auth';
import { createStyles, makeStyles, MenuItem } from '@material-ui/core';
import { getTeamUsers, updateRemittanceReceivableAssignments } from 'api/orderManagement/order-management-api';
import { RegexNumeric } from 'features/constants';
import {
  getMemberNames,
  RemittanceReceivablesCategory,
  RemittanceReceivablesColor,
  RemittanceReceivablesStatus,
  TeamAssignment,
} from 'features/orderManagement/enum';
import {
  RemittanceReceivableResponseModel,
  RemittanceReceivableUpdateRequestModel,
  UserDetailType,
} from 'features/orderManagement/types';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompleteSearch from 'ui-kit/inputs/AutocompleteSearch/AutocompleteSearch';
import { getFullName } from 'utilities/utilities';

interface FileEditAssignmentProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  data: RemittanceReceivableResponseModel[];
  onSaved?: ({
    status,
    category,
    assigneeId,
  }: {
    status: RemittanceReceivablesStatus | null;
    category: RemittanceReceivablesCategory | null;
    assigneeId: number | null;
  }) => void;
}

function firstOrDefault<T>(data: T[]): T | null {
  return data?.[0] || null;
}

function getRemittanceReceivableStatus(data: RemittanceReceivableResponseModel[]): RemittanceReceivablesStatus | null {
  const first = firstOrDefault(data)?.remitReceivableStatusName;
  return first && data?.every((datum) => datum.remitReceivableStatusName === first)
    ? RemittanceReceivablesStatus[first as keyof typeof RemittanceReceivablesStatus]
    : null;
}

function getRemittanceReceivableCategory(
  data: RemittanceReceivableResponseModel[],
): RemittanceReceivablesCategory | null {
  const first = firstOrDefault(data)?.remitReceivableCategoryName;
  return first && data?.every((datum) => datum.remitReceivableCategoryName === first)
    ? RemittanceReceivablesCategory[first as keyof typeof RemittanceReceivablesCategory]
    : null;
}

function getAssigneeId(data: RemittanceReceivableResponseModel[]): number | null {
  const first = firstOrDefault(data)?.assigneeId;
  return first && data?.every((datum) => datum.assigneeId === first) ? first : null;
}

function getOrderId(data: RemittanceReceivableResponseModel[]): number | null {
  const first = firstOrDefault(data)?.orderId;
  return first && data?.every((datum) => datum.orderId === first) ? first : null;
}

export const RemittanceReceivablesModifyAssignmentContainer = ({
  open,
  setOpen,
  data,
  onSaved,
}: FileEditAssignmentProps): JSX.Element => {
  const remittanceReceivableStatus = getRemittanceReceivableStatus(data);
  const remittanceReceivableCategory = getRemittanceReceivableCategory(data);
  const remittanceReceivableId = getAssigneeId(data);
  const remittanceOrderId = getOrderId(data);

  const [status, setStatus] = useState<RemittanceReceivablesStatus | null>(remittanceReceivableStatus);
  const [category, setCategory] = useState<RemittanceReceivablesCategory | null>(remittanceReceivableCategory);
  const [assigneeId, setAssigneeId] = useState<number | null>(remittanceReceivableId);
  const [fscUsers, setFscUsers] = useState<UserDetailType[]>([]);
  const [orderId, setOrderId] = useState<number | null>(remittanceOrderId);

  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();

  const useStyles = makeStyles(() =>
    createStyles({
      routeContainer: {
        width: '100%',
      },
      circle: {
        width: 12,
        height: 12,
        display: 'inline-block',
        marginRight: 4,
        borderRadius: 12,
      },
      separator: {
        paddingBottom: 20,
      },
    }),
  );

  const classes = useStyles();

  const updateAssignments = async (request: RemittanceReceivableUpdateRequestModel): Promise<boolean> => {
    const token = await getAccessToken();
    return await updateRemittanceReceivableAssignments(request, token);
  };

  const {
    execute: executeUpdateAssignments,
    status: executeUpdateAssignmentsStatus,
    value: executeUpdateAssignmentsValue,
    errors: executeUpdateAssignmentsErrors,
  } = useAsync<boolean>(updateAssignments, false);

  const getFscUsers = async (): Promise<Array<UserDetailType>> => {
    const token = await getAccessToken();
    const ifscMembers = await getTeamUsers(Number(TeamAssignment.IFSC), token);
    const pfscMembers = await getTeamUsers(Number(TeamAssignment.PFSC), token);
    return [...ifscMembers, ...pfscMembers];
  };

  const {
    execute: executeGetFscUsers,
    status: getFscUsersStatus,
    value: getFscUsersValue,
  } = useAsync<Array<UserDetailType>>(getFscUsers, false);

  const getStatusSelectOptions = (): SelectOption[] => {
    const selectOptions: SelectOption[] = [];
    const statuses = getMemberNames(RemittanceReceivablesStatus);
    let status = statuses.next();
    while (!status.done) {
      const statusEnum = RemittanceReceivablesStatus[status.value as keyof typeof RemittanceReceivablesStatus];
      selectOptions.push({
        value: statusEnum,
        name: status.value,
      });
      status = statuses.next();
    }
    return selectOptions;
  };

  const getCategorySelectOptions = (): SelectOption[] => {
    const selectOptions: SelectOption[] = [];
    const categories = Array.from(getMemberNames(RemittanceReceivablesCategory)).sort((a, b) => {
      return a.localeCompare(b);
    });
    for (const category of categories) {
      const categoryEnum = RemittanceReceivablesCategory[category as keyof typeof RemittanceReceivablesCategory];
      selectOptions.push({
        value: categoryEnum?.toString() || '',
        name: category,
      });
    }
    selectOptions.push({
      value: '',
      name: '',
    });
    return selectOptions;
  };

  const getAssigneeSelectOptions = (): SelectOption[] => {
    const assigneeSelectOptions: SelectOption[] = [];
    for (const fscUser of fscUsers) {
      const fullName = getFullName(fscUser);
      assigneeSelectOptions.push({
        value: fscUser.userId.toString(),
        name: fullName,
        tooltip: fscUser.username,
      });
    }
    return assigneeSelectOptions.sort((a, b) => {
      const left = a.name ?? '';
      const right = b.name ?? '';
      return left.localeCompare(right);
    });
  };

  const getStatusCustomMenuItems = (): JSX.Element[] => {
    const items: JSX.Element[] = [];
    const statuses = getMemberNames(RemittanceReceivablesStatus);
    let status = statuses.next();
    while (!status.done) {
      const statusKey = status.value;
      const statusValue = RemittanceReceivablesStatus[statusKey as keyof typeof RemittanceReceivablesStatus];
      const statusColor = RemittanceReceivablesColor[statusKey as keyof typeof RemittanceReceivablesColor];
      const menuItem = (
        <MenuItem key={statusKey} value={statusValue}>
          <span
            className={classes.circle}
            style={{ backgroundColor: '#' + statusColor.toString(16).padStart(6, '0') }}
          ></span>
          {statusKey}
        </MenuItem>
      );
      items.push(menuItem);
      status = statuses.next();
    }
    return items;
  };

  const onConfirm = () => {
    data.forEach((datum) => {
      datum.remitReceivableStatusCdId = status || RemittanceReceivablesStatus.Unassigned;
      datum.remitReceivableCategoryCdId = category || undefined;
      datum.assigneeId = assigneeId || undefined;
    });
    executeUpdateAssignments({
      remitReceivableIds: data.map((datum) => datum.remitReceivableId),
      remitReceivableStatusCdId: status || null,
      remitReceivableCategoryCdId: category || null,
      assigneeId: assigneeId || null,
      orderId: orderId || null,
    });
  };

  const onDismissAction = () => {
    setStatus(null);
    setCategory(null);
    setOpen(false);
    setAssigneeId(null);
  };

  const validateNumericEntry = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return true;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End' || e.key === 'Tab')
      return true;

    if (e.key === 'Backspace' || e.key === 'Delete') return true;

    if (RegexNumeric.test(e.key)) return true;

    e.preventDefault();
    return false;
  };

  useEffect(() => {
    if (executeUpdateAssignmentsStatus === 'success' && executeUpdateAssignmentsValue) {
      data.forEach((datum) => {
        const newStatus = status ? RemittanceReceivablesStatus[status] : '';
        const newCategory = category ? RemittanceReceivablesCategory[category] : '';
        datum.remitReceivableStatusName = newStatus;
        datum.remitReceivableCategoryName = newCategory;
        const assignee = fscUsers.find((user) => user.userId === (assigneeId ?? 0));
        datum.assigneeFirstName = assignee?.firstName || '';
        datum.assigneeLastName = assignee?.lastName || '';
        datum.assigneeFullName = getFullName({ firstName: assignee?.firstName, lastName: assignee?.lastName }) || '';
        datum.orderId = orderId || undefined;
      });
      onSaved?.({ status, category, assigneeId });
      setOpen(false);
      setStatus(null);
      setCategory(null);
      setAssigneeId(null);
      setOrderId(null);
    } else if (executeUpdateAssignmentsStatus === 'error' && executeUpdateAssignmentsErrors) {
      executeUpdateAssignmentsErrors.forEach((error: string) => {
        console.error('Error updating remittance receivable assignments:', error);
        if (error.indexOf('OrderID does not exist') !== -1) {
          addGlobalMsg({
            message: 'The provided Order ID does not exist. Please enter a valid Order ID.',
            type: 'error',
          });
        } else {
          addGlobalMsg({
            message: error,
            type: 'error',
          });
        }
      });
    }
  }, [executeUpdateAssignmentsStatus, executeUpdateAssignmentsValue]);

  useEffect(() => {
    executeGetFscUsers();
  }, []);

  useEffect(() => {
    if (getFscUsersStatus === 'success' && getFscUsersValue) {
      setFscUsers(getFscUsersValue);
    }
  }, [getFscUsersStatus, getFscUsersValue]);

  return (
    <AgentNetConfirmationDialog
      open={open}
      dialogTitle="Modify Assignment"
      dialogTextHTML={
        <>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Status"
              name="Status"
              value={status}
              options={getStatusSelectOptions()}
              data-testid="remittance-receivable-menu-status"
              required
              customMenuItems={getStatusCustomMenuItems()}
              menuOption={(value: any) => {
                setStatus(value);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AutocompleteSearch
              label="Category"
              name="Category"
              value={category?.toString() || ''}
              options={getCategorySelectOptions()}
              noMatchesLabel="No matching category found"
              onChange={(value: any) => {
                if (!value) {
                  setCategory(null);
                  return;
                }
                if (typeof value === 'object') {
                  return;
                }
                setCategory(Number(value) as RemittanceReceivablesCategory);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AutocompleteSearch
              label="Assignee"
              options={getAssigneeSelectOptions()}
              hideSearchIcon
              onChange={(value) => {
                if (!value) {
                  setAssigneeId(null);
                  return;
                }
                setAssigneeId(Number(value));
              }}
              noMatchesLabel="No matching assignee found"
              disabled={fscUsers.length === 0}
              value={assigneeId?.toString() || undefined}
              hideNoMatches
            />
          </div>
          <div className={classes.separator}>
            <AgentNetTextInput
              onKeyDown={validateNumericEntry}
              label="Order ID"
              onChange={(value) => {
                if (!value) {
                  setOrderId(null);
                  return;
                }
                const orderId = Number(value.target.value);
                setOrderId(orderId > 0 ? orderId : null);
              }}
              value={orderId?.toString() || undefined}
            />
          </div>
        </>
      }
      dialogBtnContent="Save"
      secondaryActionBtnContent="Cancel"
      onDismissAction={onDismissAction}
      onConfirm={onConfirm}
      disabled={executeUpdateAssignmentsStatus === 'pending'}
      disableBackDropClick
    />
  );
};
